import { LM, sideLandmarks, THRESHOLDS } from '@erectus/shared';
import type { Landmark, Side } from '@erectus/shared';
import { distance, toPlane } from './geometry.js';

/**
 * Framing score (0–1) — "how close is this camera setup to the ideal side view".
 *
 * PRODUCT §3.3: we deliberately do NOT store a camera snapshot to help the user
 * reproduce their setup. This number is the privacy-preserving replacement: it is
 * derived from landmarks only and lets the next session say "similar to last time".
 */
export interface FramingInput {
  landmarks: readonly Landmark[];
  side: Side;
  aspect: number;
}

export function framingScore({ landmarks, side, aspect }: FramingInput): number {
  const idx = sideLandmarks(side);
  const left = landmarks[LM.LEFT_SHOULDER];
  const right = landmarks[LM.RIGHT_SHOULDER];
  const hip = landmarks[idx.hip];
  const shoulder = landmarks[idx.shoulder];
  const nose = landmarks[LM.NOSE];
  const knee = landmarks[idx.knee];
  if (!left || !right || !hip || !shoulder || !nose) return 0;

  const torso = distance(toPlane(shoulder, aspect), toPlane(hip, aspect));
  if (torso <= 1e-6) return 0;

  // 1. Side-ness: the two shoulders should overlap in a true side view.
  const separation = Math.abs(left.x - right.x) * aspect;
  const sideness = clamp01(
    1 - separation / torso / THRESHOLDS.gate.maxShoulderSeparationRatio,
  );

  // 2. Subject size: the torso should fill a healthy share of the frame height,
  //    which is what the 1.5–2.5 m distance guidance produces in practice.
  const sizeFit = bell(torso, 0.3, 0.16);

  // 3. Vertical placement: head room above the nose, knees inside the frame.
  const headroom = clamp01((nose.y - THRESHOLDS.gate.minNoseY) / 0.12);
  const kneeInFrame = knee && knee.visibility >= THRESHOLDS.gate.kneeVisibility ? 1 : 0.6;

  // 4. Camera height: ideal is hip-to-chest height, i.e. the torso midpoint sits
  //    near the vertical centre of the frame.
  const midY = (shoulder.y + hip.y) / 2;
  const heightFit = bell(midY, 0.5, 0.25);

  return round3(0.4 * sideness + 0.2 * sizeFit + 0.2 * heightFit + 0.1 * headroom + 0.1 * kneeInFrame);
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** 1 at `centre`, falling off smoothly; 0 beyond `width`. */
function bell(value: number, centre: number, width: number): number {
  return clamp01(1 - Math.abs(value - centre) / width);
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Wording for the "set the camera like last time" hint (PRODUCT §3.3). */
export function framingComparison(current: number, previous: number | null): string | null {
  if (previous === null) return null;
  const delta = current - previous;
  if (Math.abs(delta) <= 0.08) return 'Framing looks like your last session.';
  return delta > 0
    ? 'Framing is tighter than last session — good.'
    : 'Framing differs from last session. Check the distance and camera height.';
}
